<template>
  <v-autocomplete
    :clearable="true"
    :data-cy="`service-area-user-picker-${attributeName}`"
    :dense="dense"
    :filled="filled"
    :hide-details="hideDetails"
    :hint="hint"
    :item-disabled="isUserDisabled"
    :items="sortedUserList"
    :item-text="formatUserForDisplay"
    :label="pickerLabel"
    :loading="loading"
    :outlined="outlined"
    :persistent-hint="persistentHint"
    :rules="rules"
    :value="value"
    @input="$emit('input', $event)"
    item-value="username"
  >
    <template v-slot:item="{ item }">
      <div>{{ item.firstName }} {{ item.lastName }} ({{ item.email }})</div>
    </template>
  </v-autocomplete>
</template>

<script>
import { fetchApi } from "@/api/fetchApi";

export default {
  computed: {
    hint() {
      const hasInactiveUsers = this.userList.some(user => !user.active);
      return hasInactiveUsers
        ? "Current inactive assignment can be reselected during this edit session only. Other inactive Service Area users are hidden."
        : "";
    },
    persistentHint() {
      return this.hint !== "";
    },
    pickerLabel() {
      const labelText = this.isRequired ? `${this.label} *` : this.label;
      return labelText;
    },
    rules() {
      return this.setRules();
    },
    sortedUserList() {
      // Sort users alphabetically by last name, then first name
      const users = [...this.userList];
      return users.sort((a, b) => {
        // Within same status, sort alphabetically by last name, then first name
        const aName = `${a.lastName || ""} ${a.firstName || ""}`.trim() || a.username;
        const bName = `${b.lastName || ""} ${b.firstName || ""}`.trim() || b.username;
        return aName.localeCompare(bName);
      });
    },
  },
  data() {
    return {
      abortController: null,
      initialValue: null,
      loading: false,
      userList: [],
    };
  },
  beforeDestroy() {
    // Cancel any in-flight request when component is destroyed
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    async fetchUsers() {
      if (!this.serviceAreaId) {
        return;
      }

      // Cancel any in-flight request before starting new one
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      const currentController = this.abortController;
      this.loading = true;

      try {
        const params = { serviceAreaId: this.serviceAreaId };

        // Include current value to fetch that specific user if they're inactive
        if (this.value) {
          params.selectedUsername = this.value;
        }

        const response = await fetchApi({
          method: "get",
          url: "/api/user",
          params,
          signal: currentController.signal,
        });

        this.userList = response.data;
      } catch (e) {
        // Ignore abort errors (expected when request is cancelled)
        const isAbortError = e.name === "AbortError";
        if (!isAbortError) {
          console.error(e);
          this.$notifyError({ message: "Unable to fetch user list for service area" });
        }
      } finally {
        // Only update loading state if we're still the current request
        if (this.abortController === currentController) {
          this.loading = false;
          this.abortController = null;
        }
      }
    },
    formatUserForDisplay(user) {
      const { firstName, lastName, username, email, active } = user;
      if (!username) {
        return "(Unassigned)";
      }

      if (!firstName || !lastName) {
        return active ? username : `${username} - Inactive`;
      }

      const displayName = `${firstName} ${lastName} (${email})`;
      return active ? displayName : `${displayName} - Inactive`;
    },
    isUserDisabled(user) {
      // Allow reselecting the originally-assigned user even if inactive
      if (user.username === this.initialValue) {
        return false;
      }
      // Disable all other inactive users
      return !user.active;
    },
    setRules() {
      if (this.isRequired) {
        return [v => !!v || `${this.fieldName} is required`];
      }
      return [() => true];
    },
  },
  props: {
    attributeName: {
      type: String,
      default: "",
    },
    dense: {
      type: Boolean,
      default: false,
    },
    fieldName: {
      type: String,
      default: "",
    },
    filled: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "Select user",
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    serviceAreaId: {
      type: Number,
      required: false,
    },
    value: {},
  },
  watch: {
    serviceAreaId: {
      handler(newVal) {
        if (newVal) {
          this.fetchUsers();
        }
      },
      immediate: true,
    },
    value: {
      handler(newVal) {
        // Capture the initial value only once when first set
        if (newVal && this.initialValue === null) {
          this.initialValue = newVal;
        }
      },
      immediate: true,
    },
  },
};
</script>
